import combineActionHandlers from "@web-monorepo/infra/combineActionHandlers";
import { InstallReducer } from "@web-monorepo/shared/podInfra";
import isString from "lodash/isString";
import { Saga } from "redux-saga";
import setupDelete from "./setupDelete";
import setupGet from "./setupGet";
import setupPost from "./setupPost";
import setupPut from "./setupPut";

export default function setupAll({
  makeUpdatePath,
  makePath,
  stateKey,
  makeBody,
  makeQuery,
}: {
  makeUpdatePath: (args: Record<string, unknown>) => Array<string | number | symbol> | string;
  makePath: (args: Record<string, unknown>) => string;
  stateKey: string;
  makeBody: (args: Record<string, unknown>) => Record<string, unknown>;
  makeQuery?: (args: Record<string, unknown>) => Record<string, string | boolean>;
}) {
  const makeMemberPath = (args: Record<string, unknown>) => `${makePath(args)}/${args._id}`;

  // optimistic update assumes action payload includes _id
  const makeOptimisticMemberUpdatePath = (actionPayload: Record<string, unknown> & { _id: string }) => {
    let path = makeUpdatePath(actionPayload);
    if (isString(path)) {
      path = path.split(".");
    }
    return path.concat(actionPayload._id);
  };

  // pessimistic update assumes _id is in the API response payload
  const makePessimisticMemberUpdatePath = (actionPayload: Record<string, unknown> & { response: { _id: string } }) => {
    let path = makeUpdatePath(actionPayload);
    if (isString(path)) {
      path = path.split(".");
    }
    return path.concat(actionPayload.response._id);
  };

  const collectionGet = setupGet({
    makePath,
    makeUpdatePath,
    makeQuery,
    actionName: "collectionGet",
    stateKey,
  });

  const memberGet = setupGet({
    makePath: makeMemberPath,
    makeUpdatePath: makePessimisticMemberUpdatePath,
    makeQuery,
    actionName: "memberGet",
    stateKey,
  });

  const collectionPost = setupPost({
    makePath,
    makeUpdatePath: makePessimisticMemberUpdatePath,
    makeBody,
    stateKey,
  });

  const memberPut = setupPut({
    makePath: makeMemberPath,
    makeUpdatePath: makePessimisticMemberUpdatePath,
    makeBody,
    stateKey,
  });

  const memberDelete = setupDelete({
    makePath: makeMemberPath,
    makeUpdatePath: makeOptimisticMemberUpdatePath,
    stateKey,
  });

  const setups = [memberGet, collectionGet, collectionPost, memberPut, memberDelete];

  return {
    memberGetActionCreator: memberGet.actionCreator,
    collectionGetActionCreator: collectionGet.actionCreator,
    memberPutActionCreator: memberPut.actionCreator,
    collectionPostActionCreator: collectionPost.actionCreator,
    memberDeleteActionCreator: memberDelete.actionCreator,
    installSagas: (installSaga: (saga: Saga) => void) => setups.forEach(({ saga }) => installSaga(saga)),
    installReducers: (installReducer: InstallReducer, initialState = {}) => {
      const reducers = setups.map(({ reducer }) => reducer);
      installReducer(stateKey, combineActionHandlers(initialState, reducers));
    },

    // most of the time, you shouldn't need these at all
    memberGet,
    collectionGet,
    collectionPost,
    memberPut,
    memberDelete,
  };
}
